/**
 * useEllaDucking — Shared audio ducking for Ella's TTS voice
 *
 * Ella's TTS always plays through a LOCAL Audio element (device speakers).
 * Music may play locally (WebAudioRenderer) or on a network renderer (DAC/Sonos).
 *
 * Ducking logic:
 *   - Network renderer active → no ducking needed (TTS is local, music is remote,
 *     different output devices, no conflict)
 *   - Local playback → duck the music audio element to 15%
 *
 * User preference (`maestro_valet_prefs.ellaVoiceMode`):
 *   "duck"  (default) — lower local music to 15% while TTS plays
 *   "pause" — pause music during TTS, resume after
 *   "off"   — suppress TTS entirely
 */

import { useRef, useCallback, useMemo } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { TransportCommand } from '../transport/TransportCommand';

const DUCK_RATIO = 0.15;
const SAFETY_TIMEOUT_MS = 30_000;

function getEllaVoiceMode() {
  try {
    const raw = localStorage.getItem('maestro_valet_prefs');
    if (!raw) return 'duck';
    const prefs = JSON.parse(raw);
    const mode = prefs.ellaVoiceMode;
    if (mode === 'duck' || mode === 'pause' || mode === 'off') return mode;
  } catch {}
  return 'duck';
}

function getMusicAudioElement() {
  return document.querySelector('audio[data-maestro-music]') || document.querySelector('audio');
}

function hasNetworkRenderer() {
  const store = useMaestroStore.getState();
  return !!store.activeConnectorRenderer;
}

/**
 * Imperative (non-hook) ducking API for module-scope functions like
 * SupportPage's speakText() which live outside React component scope.
 */
export function createEllaDucker() {
  let originalVolume = null;
  let wasPaused = false;
  let safetyTimer = null;

  function restore() {
    if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }

    if (originalVolume !== null) {
      const mode = getEllaVoiceMode();
      if (mode === 'duck' && !hasNetworkRenderer()) {
        const music = getMusicAudioElement();
        if (music) music.volume = Math.max(0, Math.min(1, originalVolume / 100));
      } else if (mode === 'pause' && wasPaused) {
        TransportCommand.play();
      }
      originalVolume = null;
      wasPaused = false;
    }
  }

  function startDuck() {
    const mode = getEllaVoiceMode();
    if (mode === 'off') return false;

    const storeVol = useMaestroStore.getState().volume ?? 100;
    originalVolume = storeVol;

    if (mode === 'duck') {
      if (hasNetworkRenderer()) {
        // TTS plays on local speakers, music plays on the network renderer —
        // different output devices, no conflict, nothing to duck.
      } else {
        const duckedLevel = Math.round(storeVol * DUCK_RATIO);
        const music = getMusicAudioElement();
        if (music) music.volume = Math.max(0, Math.min(1, duckedLevel / 100));
      }
    } else if (mode === 'pause') {
      const state = useMaestroStore.getState().playbackState;
      if (state === 'playing') {
        wasPaused = true;
        TransportCommand.pause();
      }
    }

    safetyTimer = setTimeout(restore, SAFETY_TIMEOUT_MS);
    return true;
  }

  function stopDuck() {
    restore();
  }

  function wrapAudio(audioEl) {
    if (!audioEl) return;
    const origOnEnded = audioEl.onended;
    const origOnError = audioEl.onerror;
    audioEl.onended = function (e) {
      restore();
      if (typeof origOnEnded === 'function') origOnEnded.call(this, e);
    };
    audioEl.onerror = function (e) {
      restore();
      if (typeof origOnError === 'function') origOnError.call(this, e);
    };
  }

  return { startDuck, stopDuck, wrapAudio, getMode: getEllaVoiceMode };
}

/**
 * React hook version — one ducker instance per component lifetime.
 * Returns { startDuck, stopDuck, wrapAudio, getMode }.
 */
export default function useEllaDucking() {
  const duckerRef = useRef(null);
  if (!duckerRef.current) {
    duckerRef.current = createEllaDucker();
  }

  const startDuck = useCallback(() => duckerRef.current.startDuck(), []);
  const stopDuck = useCallback(() => duckerRef.current.stopDuck(), []);
  const wrapAudio = useCallback((el) => duckerRef.current.wrapAudio(el), []);
  const getMode = useCallback(() => getEllaVoiceMode(), []);

  return useMemo(() => ({ startDuck, stopDuck, wrapAudio, getMode }), [startDuck, stopDuck, wrapAudio, getMode]);
}
